import { Check } from "lucide-react";
import { fileUrl } from "../lib/api";
import { useTr } from "../lib/publicI18n";

/**
 * Grille des chaînes TV regroupées (« Sports », « Français »…) : logo en couleur sur tuile blanche (ou nom si la
 * chaîne n'a pas de logo), nom en légende, badge « EN/ES » pour le double audio.
 * Mode sélection : `selected` (Set d'ids) + `onToggle(id)` ; `full` bloque l'ajout (plafond atteint).
 * `premium` : tuiles arrondies, sélection à la couleur de la société (page client premium).
 */
export default function ChannelTiles({ channels, selected, onToggle, full = false, premium = false }) {
  const tr = useTr();
  const groups = [];
  for (const c of channels || []) {
    let g = groups.find((x) => x.name === c.group);
    if (!g) groups.push((g = { name: c.group, items: [] }));
    g.items.push(c);
  }
  return (
    <div className="space-y-5" data-testid="channel-tiles">
      {groups.map((g) => (
        <div key={g.name}>
          <p className={premium ? "text-[12px] font-bold uppercase tracking-[0.06em] mb-2.5 text-muted-foreground" : "text-[11px] tracking-[0.2em] uppercase font-medium mb-2.5 text-muted-foreground"}>
            {g.name} <span className="normal-case tracking-normal font-normal">({g.items.length})</span>
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-2">
            {g.items.map((c) => {
              const isSel = !!selected?.has(c.id);
              // Bientôt disponible : visible (grisée, badge) mais pas encore au choix — sauf pour retirer un choix antérieur.
              const soon = !!c.coming_soon;
              const tile = (
                <>
                  <div className={premium
                    ? `relative aspect-[2/1] rounded-[12px] bg-white flex items-center justify-center overflow-hidden p-2 ${soon ? "pt-5" : ""} transition ${isSel ? "ring-2 ring-primary shadow-[0_8px_18px_-12px_hsl(var(--primary)/0.9)]" : "border border-[#E3DED5]"}`
                    : `relative aspect-[2/1] rounded-xl bg-white flex items-center justify-center overflow-hidden p-2 ${soon ? "pt-5" : ""} border border-black/10 ${isSel ? "ring-2 ring-primary ring-offset-2" : ""}`}>
                    {c.logo_file_id
                      ? <img src={fileUrl(c.logo_file_id)} alt="" loading="lazy" className={`max-w-full max-h-full object-contain ${soon ? "grayscale opacity-50" : ""}`} />
                      : <span className={`text-[11px] font-semibold text-center px-1 leading-tight ${soon ? "text-muted-foreground" : "text-foreground"}`}>{c.name}</span>}
                    {soon && (
                      <span className="absolute top-1 left-1 px-1.5 py-px rounded-full bg-foreground/80 text-white text-[9px] font-semibold leading-tight" data-testid={`channel-soon-${c.id}`}>
                        {tr("Bientôt", "Soon")}
                      </span>
                    )}
                    {isSel && (
                      <span className={`absolute top-1 right-1 rounded-full bg-primary text-white flex items-center justify-center ${premium ? "w-5 h-5" : "w-4 h-4"}`}>
                        <Check className="w-3 h-3" strokeWidth={premium ? 3 : 2} />
                      </span>
                    )}
                  </div>
                  <p className={`mt-1 text-[11px] leading-tight truncate ${premium && isSel ? "font-semibold" : ""} ${soon ? "text-muted-foreground" : ""}`} title={c.name}>
                    {c.name}
                    {soon && <span className="sr-only"> {tr("(bientôt disponible)", "(coming soon)")}</span>}
                    {c.dual_audio && <span className="ml-1 text-[9px] font-semibold text-muted-foreground" title={tr("Double audio anglais / espagnol", "Dual audio English / Spanish")}>EN/ES</span>}
                  </p>
                </>
              );
              return onToggle ? (
                <button key={c.id} type="button" onClick={() => onToggle(c.id)} aria-pressed={isSel} disabled={(full || soon) && !isSel}
                  title={soon ? tr("Bientôt disponible", "Coming soon") : undefined}
                  data-testid={`channel-${c.id}`} className="text-left rounded-sm disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                  {tile}
                </button>
              ) : (
                <div key={c.id} data-testid={`channel-${c.id}`}>{tile}</div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Chaînes choisies par le client après signature, par formule (écrans staff et partenaire). */
export function ChannelChoices({ choices }) {
  return (
    <div className="pt-3 border-t border-border" data-testid="channel-choices">
      <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-2">Chaînes TV</p>
      {choices.map((l) => (
        <div key={l.item_id} className="text-sm mb-2" data-testid={`channel-choice-${l.item_id}`}>
          <p className="font-medium">
            {l.name} —{" "}
            {l.channels.length === l.count
              ? <span className="text-emerald-700">{l.count} chaînes choisies</span>
              : <span className="text-muted-foreground">à choisir par le client ({l.channels.length}/{l.count})</span>}
          </p>
          {l.channels.length > 0 && <p className="text-xs text-muted-foreground mt-0.5">{l.channels.join(", ")}</p>}
        </div>
      ))}
    </div>
  );
}
